import React from 'react';
import { Box, Heading, Icon, Tooltip } from '@zillow/constellation-10';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import { StyledContainer } from './StyledComponents';
import { EVENT_IDS } from '../../model/ClickstreamEvents';
import type {
  AdproductsZuoraBillingInvoiceDto,
  AdproductsZuoraBillingInvoiceItemDto,
} from '@zg-rentals/ts-adproducts-client';
import { IconCalendarOutline } from '@zillow/constellation-icons';

const mapDaysSpotlighted = (daysSpotlighted: Array<{ year: number; month: number; day: number }>) => {
  if (!daysSpotlighted || daysSpotlighted.length === 0) {
    return '-';
  }
  return daysSpotlighted.map((date) => {
    const dateStr = `${date.month}/${date.day}/${date.year}`;
    return (
      <div key={dateStr} className="InvoicePage-active-range">
        {dateStr}
      </div>
    );
  });
};

export interface ListingSpotlightTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const ListingSpotlightTable = ({ currentInvoice }: ListingSpotlightTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();

  const invoiceColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceItemDto>> = [
    {
      width: 4,
      header: 'Address',
      dataKey: 'address',
      align: 'left',
    },
    {
      width: 2,
      header: 'Unit',
      dataKey: 'unit',
      align: 'left',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        return data.unit || '-';
      },
    },
    {
      width: 2,
      align: 'left',
      header: 'Product',
      customKey: 'product',
      renderer: () => {
        return 'Listing Spotlight';
      },
    },
    {
      width: 3,
      header: 'Total Days Spotlighted',
      dataKey: 'totalSpotlightedDays',
      align: 'right',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        const { totalSpotlightedDays, daysSpotlighted } = data;
        if (!totalSpotlightedDays) return '-';
        return (
          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip
              trigger={
                <Box>
                  {totalSpotlightedDays}
                  <Icon size="md">
                    <IconCalendarOutline />
                  </Icon>
                </Box>
              }
            >
              {mapDaysSpotlighted(daysSpotlighted)}
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceItemDto>> = [
    {
      width: 5,
      header: 'Address',
      dataKey: 'address',
      align: 'left',
    },
    {
      width: 2,
      header: 'Unit',
      dataKey: 'unit',
      align: 'left',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        return data.unit || '-';
      },
    },
    {
      width: 4,
      header: 'Total Days',
      dataKey: 'totalSpotlightedDays',
      align: 'right',
    },
  ];

  const columnConfig = isScreenDesktop ? invoiceColumnConfig : mobileColumnConfig;

  const spotlightBreakdowns = (currentInvoice?.sfhLineItems ?? []).filter((item) => item.totalSpotlightedDays > 0);

  if (spotlightBreakdowns.length === 0) {
    return null;
  }

  return (
    <StyledContainer data-eventid={EVENT_IDS.SET_FILTER} data-eventdescription="Set filter event">
      <Heading level={4}>Listing Spotlight</Heading>
      <Box css={{ margin: 'xs' }} />
      <AdvancedTable columnConfig={columnConfig} data={spotlightBreakdowns} isMobile={!isScreenDesktop} />
      <Box css={{ margin: 'xs' }} />
    </StyledContainer>
  );
};
